// frontend/src/api/marketingCopy.js
import apiClient from './client';

const logger = console;

/**
 * Preview marketing copy for a list of UPCs
 * @param {string[]} upcs - List of UPCs to preview
 * @param {string} recipientPartyId - Optional party ID for recipient-specific data (e.g., Kroger)
 * @returns {Promise<Array>} Array of preview results
 */
export const previewMarketingCopy = async (upcs, recipientPartyId = null) => {
  try {
    logger.info("API Call: previewMarketingCopy, UPCs:", upcs.length, "recipientPartyId:", recipientPartyId);
    const payload = { upcs };
    if (recipientPartyId) {
      payload.recipient_party_id = recipientPartyId;
    }
    const response = await apiClient.post('/marketing-copy/preview', payload);
    return response.data;
  } catch (error) {
    logger.error("Error previewing marketing copy:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to preview marketing copy");
  }
};

/**
 * Find and replace text in marketing copy
 * @param {Object} params - Find/replace parameters
 * @param {string[]} params.upcs - List of UPCs
 * @param {string} params.find_text - Text to find
 * @param {string} params.replace_text - Text to replace with
 * @param {string} params.field - Field to search (default: "Long Item Description")
 * @param {boolean} params.case_sensitive - Case sensitive search (default: false)
 * @param {boolean} params.preview_only - Preview only without updating (default: true)
 * @param {string} params.recipient_party_id - Optional party ID for recipient-specific data
 * @returns {Promise<Object>} Find/replace results
 */
export const findReplaceMarketingCopy = async ({
  upcs,
  find_text,
  replace_text,
  field = "Long Item Description",
  case_sensitive = false,
  preview_only = true,
  recipient_party_id = null
}) => {
  try {
    logger.info("API Call: findReplaceMarketingCopy", {
      upcs_count: upcs.length,
      find_text,
      replace_text,
      field,
      preview_only,
      recipient_party_id
    });
    const payload = {
      upcs,
      find_text,
      replace_text,
      field,
      case_sensitive,
      preview_only
    };
    if (recipient_party_id) {
      payload.recipient_party_id = recipient_party_id;
    }
    const response = await apiClient.post('/marketing-copy/find-replace', payload);
    return response.data;
  } catch (error) {
    logger.error("Error in find/replace:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to perform find/replace");
  }
};

/**
 * Update marketing copy for multiple UPCs with specific values
 * @param {Array<{upc: string, field: string, new_value: string}>} updates - List of updates
 * @returns {Promise<Array>} Update results
 */
export const updateMarketingCopy = async (updates) => {
  try {
    logger.info("API Call: updateMarketingCopy, Updates:", updates.length);
    const response = await apiClient.post('/marketing-copy/update', { updates });
    return response.data;
  } catch (error) {
    logger.error("Error updating marketing copy:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to update marketing copy");
  }
};

/**
 * Get available marketing copy fields
 * @returns {Promise<Object>} Available fields
 */
export const getMarketingCopyFields = async () => {
  try {
    logger.info("API Call: getMarketingCopyFields");
    const response = await apiClient.get('/marketing-copy/fields');
    return response.data;
  } catch (error) {
    logger.error("Error fetching fields:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to fetch fields");
  }
};
